import { ReactNode } from 'react';
import Icon from '../Icon';
import { String } from '../../string';
import { useGetString } from '../../../hooks/useGetString';

interface Props {
  peopleHeader: ReactNode;
  timeHeader: ReactNode;
  dateHeader: ReactNode;
  people: ReactNode;
  time: ReactNode;
  date: ReactNode;
  seatings: ReactNode;
  tableLocation?: ReactNode;
  isCancelled?: boolean;
  editable?: boolean;
  isWaitlist?: boolean;
  /** Makes the date the "add to calendar" control. */
  onCalendarClick?: () => void;
  calendarLabel?: string;
  /** Same left inset as the ticket header (the 32px notch). */
  edgeToEdge?: boolean;
}

export default function BookingSummary({
  peopleHeader,
  timeHeader,
  dateHeader,
  people,
  time,
  date,
  seatings,
  tableLocation,
  isCancelled,
  editable,
  isWaitlist,
  onCalendarClick,
  calendarLabel,
  edgeToEdge = false,
}: Props) {
  const getString = useGetString();

  return (
    <>
      {tableLocation && (
        <h2 className="w-full text-center font-light text-darkGold mb-3 uppercase tracking-huge text-sm break-words">
          {tableLocation}
        </h2>
      )}
      <div
        className={`grid grid-cols-2 gap-y-4 w-full ${
          edgeToEdge ? 'pl-8' : ''
        }`}
      >
        <dl
          className={`uppercase flex flex-col font-[550] min-w-0 ${isCancelled ? 'opacity-50' : ''}`}
          tabIndex={0}
          aria-label={`${peopleHeader} ${seatings} ${people}`}
          role="group"
        >
          <dt
            className="text-xs opacity-50 flex items-center gap-1 mb-1 tracking-wider"
            aria-hidden="true"
          >
            <Icon type="persons" className="w-4 h-3 shrink-0" aria-hidden="true" />
            {peopleHeader}
          </dt>
          <dd
            className={`text-base leading-5 tracking-wider ${isCancelled ? 'line-through' : ''}`}
            aria-hidden="true"
          >
            {seatings} {people}
          </dd>
        </dl>
        <dl
          className={`uppercase flex flex-col font-[550] min-w-0 ${isCancelled ? 'opacity-50' : ''}`}
          tabIndex={0}
          aria-label={`${timeHeader} ${time}`}
          role="group"
        >
          <dt
            className="text-xs opacity-50 flex items-center gap-1 mb-1 tracking-wider"
            aria-hidden="true"
          >
            <Icon type="clock" className="w-3 h-3 shrink-0" aria-hidden="true" />
            {timeHeader}
          </dt>
          <dd
            className={`text-base leading-5 tracking-wider whitespace-nowrap ${isCancelled ? 'line-through' : ''}`}
            aria-hidden="true"
          >
            {time}
          </dd>
        </dl>
        <dl
          className="col-span-2 uppercase flex flex-col justify-between font-[550]"
          {...(onCalendarClick
            ? {}
            : {
                tabIndex: 0,
                role: 'group',
                'aria-label': `${dateHeader} ${date} ${isCancelled ? getString('cancelled') : ''}`,
              })}
        >
          <dt
            className={`text-xs flex items-center gap-1 mb-1 tracking-wider ${isCancelled ? 'opacity-25' : 'opacity-50'}`}
            aria-hidden="true"
          >
            <Icon type="date" className="w-3 h-3 shrink-0" aria-hidden="true" />
            {dateHeader}
          </dt>
          <dd className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={`text-base leading-5 tracking-wider ${isCancelled ? 'line-through opacity-50' : ''}`}
              aria-hidden={!onCalendarClick}
            >
              {date}
            </span>
            {onCalendarClick && (
              <button
                type="button"
                data-testid="add-to-calendar"
                onClick={onCalendarClick}
                aria-label={calendarLabel}
                className="relative z-20 inline-flex items-center gap-1 text-sm font-normal normal-case tracking-normal text-darkGold underline decoration-dotted underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-darkGold"
              >
                <Icon type="calendarBlank" className="w-3 h-3 shrink-0" aria-hidden="true" />
                {getString('labels.add-to-calendar')}
              </button>
            )}
            {isCancelled && (
              <span className="text-[#ff5636] font-[550] tracking-wide text-base leading-5 uppercase whitespace-nowrap ml-4">
                <String id="cancelled" />
              </span>
            )}
          </dd>
        </dl>
      </div>
    </>
  );
}
